import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export const EVENT_INTELLIGENCE_ACCESS_QUERY_KEY = 'event-intelligence-access';

export interface EventIntelligenceAccess {
  enabled: boolean;
  isLoading: boolean;
}

export interface EventIntelligenceAccessResponse {
  enabled: boolean;
  /** The server can read badge photos with its vision model for this user. */
  badgeVisionRead?: boolean;
  /** Live badge reads stream several frames at a time (event-badge-stream flag); needs badgeVisionRead. */
  badgeStreamRead?: boolean;
  /** The phone detects text in the frame before streaming it (event-badge-text-detect flag); needs badgeStreamRead. */
  badgeTextDetect?: boolean;
  /** Book now books through Chili Piper's API in a native sheet (event-native-booking flag + API key). */
  nativeBooking?: boolean;
}

// Shared by every hook that reads the access answer, so they hit one cached request.
export const eventIntelligenceAccessQuery = (userId: string | undefined) => ({
  queryKey: [EVENT_INTELLIGENCE_ACCESS_QUERY_KEY, userId],
  queryFn: async () => (await api.get<EventIntelligenceAccessResponse>('/event-intelligence/access')).data,
  enabled: !!userId,
  staleTime: 5 * 60_000,
  retry: false,
});

// The `event-intelligence` PostHog flag is read by the API, not by posthog-js: the server is the
// gate that 404s every event route, so asking it keeps the UI and API in agreement, and the
// Development-only MERIDIAN_FEATURE_FLAG_OVERRIDES reaches both. Fails closed on any error.
export function useEventIntelligenceAccess(): EventIntelligenceAccess {
  const { user, isLoading: authLoading } = useAuth();
  const query = useQuery(eventIntelligenceAccessQuery(user?.id));

  return {
    enabled: query.data?.enabled === true,
    isLoading: authLoading || (!!user && query.isPending),
  };
}
